import type { JSX, ReactNode } from "react";
import { Text, View } from "@/components";
import cn from "@/utils/cn";

export interface LandingSectionProps {
  /** Título de la sección; si falta, solo se renderiza el contenido. */
  title?: string;
  /** h1 para el título de la página, h2 para secciones, h3 para subsecciones. */
  headingLevel?: "h1" | "h2" | "h3";
  id?: string;
  className?: string;
  children: ReactNode;
}

const headingSizes: Record<
  NonNullable<LandingSectionProps["headingLevel"]>,
  string
> = {
  h1: "text-3xl",
  h2: "text-2xl",
  h3: "text-xl"
};

/**
 * @name LandingSection
 * @description Sección de contenido de la landing pública: heading con el
 * acento institucional (borde dorado) y contenido libre debajo. Unifica la
 * jerarquía visual de las pestañas informativas sin imponer estructura al
 * contenido (párrafos, listas u otros organismos van como children).
 *
 * @param {LandingSectionProps} props - Título, nivel de heading y contenido.
 * @returns {JSX.Element} La sección renderizada.
 */
const LandingSection = ({
  title,
  headingLevel = "h2",
  id,
  className,
  children
}: LandingSectionProps): JSX.Element => (
  <View type="section" id={id} className={cn("flex flex-col gap-4", className)}>
    {title && (
      <Text
        type={headingLevel}
        className={cn(
          "font-semibold text-primary dark:text-foreground border-l-4 border-navbar-border pl-3",
          headingSizes[headingLevel]
        )}
      >
        {title}
      </Text>
    )}
    {children}
  </View>
);

export default LandingSection;
